/**
 * Team, Sign-offs (plan docs/plans/lms-plan.md 7.4, 13 tab 6; Appendix A
 * GET /admin/signoff-requests and POST /admin/signoffs; 18.15, 18.21, 18.22;
 * R18, R20; WP-45).
 *
 * Replaces WP-03's placeholder in place: tabs/team/subviews.ts imports this
 * file's DEFAULT export (no props), mounted at /lms/team/signoffs/* (18.3).
 *
 * The queue of competency sign-off requests from learners the admin reaches
 * at any of the admin's sites (18.21), oldest first, with the course, who was
 * asked (a named observer or any admin) and the latest observation. Open
 * requests are recorded from here (SignoffDialog: the checklist, the outcome
 * and the signature with the password); done and cancelled requests are the
 * history. A person cannot observe their own competency, and a leaver
 * (18.21: listed, flagged) cannot be signed off.
 */
import { useState } from 'react';
import { ClipboardCheck } from 'lucide-react';
import EmptyState from '../../../../ui/EmptyState';
import Modal from '../../../../ui/Modal';
import { chipCls, primaryBtnCls, secondaryBtnCls } from '../../../../ui/styles';
import { useToast } from '../../../../ui/Toast';
import { useAuth } from '../../../../contexts/AuthContext';
import { listSignoffQueue } from '../../../api/team';
import type { AdminSignoffRequest, RecordSignoffResult, SignoffRequestStatus } from '../../../api/team';
import { Pill } from '../../../components/StatusPill';
import {
  formatDay,
  metaCls,
  personName,
  tableCardCls,
  tableCls,
  tbodyCls,
  tdCls,
  theadCls,
  thCls,
} from '../../../components/pickers/shared';
import { LeaverTag, LoadFailed, Loading, useAsync } from '../matrix/shared';
import { OUTCOME_WORDS, SignoffDialog } from './SignoffDialog';

const STATUSES: Array<{ value: SignoffRequestStatus; label: string }> = [
  { value: 'open', label: 'Waiting' },
  { value: 'done', label: 'Done' },
  { value: 'cancelled', label: 'Cancelled' },
];

/** Rows per page. */
export const SIGNOFF_PAGE = 50;

export function SignoffsView() {
  const { toast } = useToast();
  const { user } = useAuth();
  const [status, setStatus] = useState<SignoffRequestStatus>('open');
  const [offset, setOffset] = useState(0);
  const [open, setOpen] = useState<AdminSignoffRequest | null>(null);
  const queue = useAsync(
    () => listSignoffQueue({ status, limit: SIGNOFF_PAGE, offset }),
    `${status}:${offset}`,
    'The sign-off requests could not be loaded.'
  );
  const items = queue.data?.items ?? [];
  const page = queue.data?.page;

  const done = (result: RecordSignoffResult) => {
    const who = open?.learner ? personName(open.learner) : 'The learner';
    const course = open?.course?.title ?? 'the course';
    setOpen(null);
    if (result.signoff.outcome === 'not_yet') {
      toast(`Recorded as not yet for ${who}. The request stays open.`, { type: 'success' });
    } else if (result.completion) {
      toast(`Sign-off recorded: ${who} is competent and ${course} is complete.`, { type: 'success' });
    } else {
      toast(`Sign-off recorded: ${who} is competent. ${course} completes once the quiz is passed.`, { type: 'success' });
    }
    queue.reload();
  };

  return (
    <div className="space-y-4" data-testid="lms-signoffs">
      <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Request state">
        {STATUSES.map((s) => (
          <button
            key={s.value}
            type="button"
            className={chipCls(status === s.value)}
            aria-pressed={status === s.value}
            onClick={() => {
              setStatus(s.value);
              setOffset(0);
            }}
            data-testid={`signoff-status-${s.value}`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {queue.error ? <LoadFailed message={queue.error} onRetry={queue.reload} testId="signoffs-error" /> : null}
      {!queue.data && queue.loading ? <Loading rows={3} /> : null}
      {queue.data && items.length === 0 ? (
        <EmptyState
          icon={ClipboardCheck}
          variant="card"
          title={status === 'open' ? 'No sign-off is waiting' : 'Nothing here yet'}
          hint={status === 'open' ? 'When a learner asks for a competency sign-off, the request appears here.' : undefined}
        />
      ) : null}

      {items.length > 0 ? (
        <div className={tableCardCls}>
          <table className={tableCls}>
            <thead className={theadCls}>
              <tr>
                <th scope="col" className={thCls}>
                  Learner
                </th>
                <th scope="col" className={thCls}>
                  Course
                </th>
                <th scope="col" className={thCls}>
                  Requested
                </th>
                <th scope="col" className={thCls}>
                  Observer
                </th>
                <th scope="col" className={thCls}>
                  Latest observation
                </th>
                <th scope="col" className={thCls}>
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className={tbodyCls}>
              {items.map((r) => {
                const self = Boolean(user?.id) && r.user_id === user?.id;
                const left = r.learner?.deleted === true;
                const last = r.last_signoff;
                return (
                  <tr key={r.id} data-testid={`signoff-row-${r.id}`}>
                    <td className={tdCls}>
                      <div className="font-medium">
                        {r.learner ? personName(r.learner) : 'Unknown person'}
                        <LeaverTag deleted={left} />
                      </div>
                      {r.learner?.email && r.learner.full_name ? <div className={metaCls}>{r.learner.email}</div> : null}
                    </td>
                    <td className={tdCls}>{r.course?.title ?? 'Course'}</td>
                    <td className={tdCls}>{formatDay(r.requested_at)}</td>
                    <td className={tdCls}>{r.observer ? personName(r.observer) : 'Any admin'}</td>
                    <td className={tdCls}>
                      {last ? (
                        <>
                          <Pill tone={last.outcome === 'competent' ? 'green' : 'amber'}>{OUTCOME_WORDS[last.outcome] ?? last.outcome}</Pill>
                          <div className={metaCls}>{formatDay(last.observed_at)}</div>
                          {last.notes ? <div className={metaCls}>{last.notes}</div> : null}
                        </>
                      ) : (
                        <span className={metaCls}>Not observed yet</span>
                      )}
                    </td>
                    <td className={tdCls}>
                      {r.status === 'open' ? (
                        self || left ? (
                          <span className={metaCls} data-testid={`signoff-blocked-${r.id}`}>
                            {self ? 'Another admin observes your own request' : 'This person has left'}
                          </span>
                        ) : (
                          <button type="button" className={primaryBtnCls} onClick={() => setOpen(r)} data-testid={`signoff-record-${r.id}`}>
                            Record sign-off
                          </button>
                        )
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}

      {page && (page.offset > 0 || page.hasMore) ? (
        <div className="flex justify-end gap-2">
          <button type="button" className={secondaryBtnCls} disabled={page.offset === 0 || queue.loading} onClick={() => setOffset(Math.max(0, page.offset - SIGNOFF_PAGE))}>
            Previous
          </button>
          <button type="button" className={secondaryBtnCls} disabled={!page.hasMore || queue.loading} onClick={() => setOffset(page.offset + SIGNOFF_PAGE)}>
            Next
          </button>
        </div>
      ) : null}

      <Modal isOpen={open !== null} onClose={() => setOpen(null)} title={open?.course?.title ? `Sign-off: ${open.course.title}` : 'Record a sign-off'}>
        {open ? <SignoffDialog request={open} onCancel={() => setOpen(null)} onDone={done} /> : null}
      </Modal>
    </div>
  );
}

export default SignoffsView;
